/**
 * OfflineContext - Manages network connectivity, offline data queue, and sync state.
 *
 * This context is the central hub for all offline/PWA functionality:
 *	 1. Monitors browser online/offline status via navigator.onLine + event listeners
 *	 2. Tracks the count of pending items in IndexedDB (polled every 5s when offline)
 *	 3. Exposes a triggerSync() method that calls syncService.syncAll() on demand
 *	 4. Provides addPendingItem() for other components to queue offline data
 *	 5. Tracks isSyncing and syncResults state for the SyncStatusBar UI
 *
 * Provider nesting: BrowserRouter > AuthProvider > AppProvider > OfflineProvider
 *
 * Usage: Wrap your app in <OfflineProvider>, then consume with useOfflineSync().
 */
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
	getPendingCount,
	addPendingItem as storageAddPendingItem,
	StoreName,
} from '../services/offlineStorage';
import { syncAll, SyncReport } from '../services/syncService';

/**
 * OfflineContextValue - Shape of the value exposed by OfflineContext.
 *
 * @property isOnline		 - True when the browser reports network connectivity
 * @property pendingCount	 - Total number of offline items awaiting sync across all stores
 * @property isSyncing		 - True while a sync operation is in progress
 * @property syncResults	 - Results from the last sync attempt (null if no sync yet)
 * @property triggerSync	 - Manually initiates a sync of all pending offline changes
 * @property addPendingItem	 - Queue a new item in a specific offline store
 * @property refreshPendingCount - Force a re-read of the pending count from IndexedDB
 */
export interface OfflineContextValue {
	isOnline: boolean;
	pendingCount: number;
	isSyncing: boolean;
	syncResults: SyncReport | null;
	triggerSync: () => Promise<void>;
	addPendingItem: (storeName: StoreName, data: Record<string, unknown>) => Promise<number>;
	refreshPendingCount: () => Promise<void>;
}

// Create the context with undefined default so we can detect missing providers
export const OfflineContext = createContext<OfflineContextValue | undefined>(undefined);

/**
 * OfflineProvider - Wraps child components with offline state management.
 *
 * Mounts event listeners for online/offline detection, sets up a polling
 * interval to refresh the pending count, and provides sync controls.
 */
export const OfflineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	// --- Network status state ---
	// Initialize with the browser's current connectivity status
	const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

	// --- Pending item count ---
	// Total number of offline items awaiting sync across all IndexedDB stores
	const [pendingCount, setPendingCount] = useState<number>(0);

	// --- Sync state ---
	// True while syncAll() is running; prevents double-sync
	const [isSyncing, setIsSyncing] = useState<boolean>(false);

	// --- Last sync results ---
	// Populated after triggerSync() completes; null until first sync
	const [syncResults, setSyncResults] = useState<SyncReport | null>(null);

	// TanStack Query client — used to invalidate cached server reads after a
	// successful sync so the UI reflects data the server just accepted.
	const queryClient = useQueryClient();

	// Ref to hold the polling interval ID so we can clean it up
	const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// --- Browser connectivity event listeners ---
	// Attach "online" and "offline" listeners on mount, clean up on unmount.
	// These fire whenever the browser detects a network change.
	useEffect(() => {
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);

		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);

		// Cleanup: remove listeners when the provider unmounts
		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	/**
	 * refreshPendingCount() - Reads the current pending item count from IndexedDB
	 * and updates the pendingCount state. Called on mount, after sync, and
	 * periodically when offline.
	 */
	const refreshPendingCount = useCallback(async () => {
		try {
			const count = await getPendingCount();
			setPendingCount(count);
		} catch (error) {
			// IndexedDB access can fail in private browsing / storage-full scenarios
			console.error('Failed to read pending count from IndexedDB:', error);
		}
	}, []);

	// --- Initial pending count load ---
	// Read the count once on mount so the UI immediately reflects any
	// items left over from a previous session
	useEffect(() => {
		refreshPendingCount();
	}, [refreshPendingCount]);

	// --- Polling interval for pending count ---
	// When offline, poll every 5 seconds to keep the badge up to date
	// as the user creates new items. When online, no polling needed
	// (the count updates via refreshPendingCount after each addPendingItem).
	useEffect(() => {
		// Clear any existing interval before setting a new one
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}

		if (!isOnline) {
			// Poll every 5 seconds when offline
			pollingIntervalRef.current = setInterval(() => {
				refreshPendingCount();
			}, 5000);
		}

		// Cleanup: clear the interval when the effect re-runs or unmounts
		return () => {
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}
		};
	}, [isOnline, refreshPendingCount]);

	/**
	 * triggerSync() - Manually initiates a sync of all pending offline changes.
	 *
	 * Only runs if the browser is currently online and no sync is already
	 * in progress. Updates isSyncing and syncResults state for the UI.
	 * After sync completes (success or partial failure), refreshes the
	 * pending count to reflect any items that remain.
	 */
	const triggerSync = useCallback(async () => {
		// Guard: don't sync when offline or already syncing
		if (!isOnline || isSyncing) {
			return;
		}

		setIsSyncing(true);
		// Clear previous results while syncing
		setSyncResults(null);

		try {
			// Execute the full sync across all stores
			const report = await syncAll();

			// Store the results so the SyncStatusBar can display them
			setSyncResults(report);

			// If anything was pushed to the server, the cached server reads are
			// now stale — invalidate everything so list/detail queries refetch
			// and reflect the just-synced writes. Cheap: only active queries
			// refetch immediately; the rest are marked stale for next use.
			if (report.synced > 0) {
				await queryClient.invalidateQueries();
			}
		} catch (error) {
			// Unexpected error (not per-item failures, which are handled in syncService)
			setSyncResults({
				synced: 0,
				failed: 0,
				errors: [error instanceof Error ? error.message : 'Sync failed unexpectedly'],
			});
		} finally {
			// Always mark sync as complete and refresh the count
			setIsSyncing(false);
			await refreshPendingCount();
		}
	}, [isOnline, isSyncing, refreshPendingCount, queryClient]);

	/**
	 * addPendingItem() - Convenience method to queue a new offline item.
	 *
	 * Delegates to the offlineStorage service and then refreshes the
	 * pending count so the UI badge updates immediately.
	 *
	 * @param storeName - Which IndexedDB store to write to
	 * @param data		- The payload to store
	 * @returns The auto-generated numeric id of the new record
	 */
	const addPendingItem = useCallback(async (
		storeName: StoreName,
		data: Record<string, unknown>
	): Promise<number> => {
		// Write to IndexedDB via the storage service
		const id = await storageAddPendingItem(storeName, data);

		// Immediately refresh the count so the badge updates
		await refreshPendingCount();

		return id;
	}, [refreshPendingCount]);

	// --- Context value ---
	// Memoization is not strictly needed here because the provider re-renders
	// only when state changes, but the object is kept flat for readability.
	const contextValue: OfflineContextValue = {
		isOnline,
		pendingCount,
		isSyncing,
		syncResults,
		triggerSync,
		addPendingItem,
		refreshPendingCount,
	};

	return (
		<OfflineContext.Provider value={contextValue}>
			{children}
		</OfflineContext.Provider>
	);
};
